import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { MachineDto } from 'api/src/core/schemas/MachineSchema';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import {
  Button,
  FieldGroup,
  NoItemsBox,
  showMessageBox,
  TextField,
  Title,
} from '../../../components';
import { useStore } from '../../../store';
import { formatTime } from '../../../utils/date';
import { onSuccess } from '../../../utils/errors';
import PulseKey, { type PulseKeyLicense } from '../store/PulseKey';

export default observer(function LicenseSection({
  license,
  onChange,
  pulseKey,
}: {
  license: PulseKeyLicense;
  onChange: () => void;
  pulseKey: PulseKey;
}) {
  const { pulseKeyStore } = useStore();
  const savedSeatLimit = String(license.seatLimit ?? '');
  const [seatLimit, setSeatLimit] = useState(savedSeatLimit);
  const activeCount = license.machines.filter(isActive).length;

  async function saveSeatLimit() {
    await pulseKeyStore.updateLicense(
      pulseKey,
      seatLimit ? Number(seatLimit) : undefined,
    );
    onSuccess('Seat limit saved');
    onChange();
  }

  async function reset(machine: MachineDto) {
    const buttonId = await showMessageBox({
      buttons: [
        { id: 'ok', title: 'reset' },
        { id: 'cancel', title: 'cancel' },
      ],
      content: `The product will stop working on ${machine.machineName}.`,
      title: 'Reset machine',
    });

    if (buttonId === 'ok') {
      await pulseKeyStore.unregisterMachine(pulseKey, machine.machineId);
      onChange();
    }
  }

  return (
    <>
      <Title sx={{ mb: 0 }}>
        License: {activeCount} of{' '}
        {license.seatLimit ?? license.productSettings.seatLimit} seats used
      </Title>

      <FieldGroup>
        <TextField
          helperText={`Empty uses the product default: ${license.productSettings.seatLimit}`}
          InputLabelProps={{ shrink: true }}
          label="Seat limit"
          name="seatLimit"
          onChange={(e) => setSeatLimit(e.target.value)}
          placeholder={`Default: ${license.productSettings.seatLimit}`}
          sx={{ width: 240 }}
          type="number"
          value={seatLimit}
        />
        <Button
          disabled={seatLimit === savedSeatLimit}
          onClick={saveSeatLimit}
          type="primaryText"
        >
          Save
        </Button>
      </FieldGroup>

      <Table size="small" sx={{ ...alignLeft, mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Machine</TableCell>
            <TableCell>OS</TableCell>
            <TableCell>Last seen</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>IP</TableCell>
            <TableCell>Lease expires</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {license.machines.map((machine) => (
            <TableRow key={machine.machineId}>
              <TableCell>{machine.machineName}</TableCell>
              <TableCell>{machine.os}</TableCell>
              <TableCell>
                {machine.lastSeenAt && formatTime(machine.lastSeenAt)}
              </TableCell>
              <TableCell>{machine.location}</TableCell>
              <TableCell>{machine.ip}</TableCell>
              <TableCell>{formatExpiry(machine)}</TableCell>
              <TableCell align="right">
                <Button onClick={() => reset(machine)} type="secondary">
                  Reset
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {license.machines.length === 0 && (
        <NoItemsBox>
          No machines yet. They appear after the customer activates the plugin.
        </NoItemsBox>
      )}
    </>
  );
});

/** Lines the first column up with the headings and fields */
export const alignLeft = {
  '& td:first-of-type, & th:first-of-type': { pl: 0 },
};

function isActive({ expiresAt }: MachineDto) {
  return Date.parse(expiresAt ?? '') > Date.now();
}

function formatExpiry({ expiresAt, offline }: MachineDto) {
  if (!expiresAt) {
    return '';
  }

  const date = formatTime(expiresAt);
  if (Date.parse(expiresAt) < Date.now()) {
    return `Expired ${date}`;
  }

  return offline ? `${date} (offline)` : date;
}
